import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import ReportsSection from '../components/ReportsSection';
import VisaApplicationFilters from '../components/VisaApplicationFilters';
import VisaApplicationsPoolSection from '../components/VisaApplicationsPoolSection';
import VisaApplicationHistoryModal from '../components/VisaApplicationHistoryModal';
import ActionsMenuButton from '../components/ActionsMenuButton';
import CountryMultiSelect from '../components/CountryMultiSelect';
import AirlineMultiSelect from '../components/AirlineMultiSelect';
import {
  DEFAULT_VISA_FILTERS,
  ERROR_POOL_FILTERS,
  FINALIZE_POOL_FILTERS,
  filterVisaApplications,
  getApplicantName,
} from '../utils/visaApplicationFilters';
import { parseCountryIds, serializeCountryIds } from '../utils/countryUtils';
import { formatStatusLabel, getStatusBadgeClass } from '../utils/visaApplicationStatus';
import { getVisaHistoryUrl } from '../utils/visaApplicationHistoryApi';
import VisaNotesSection from '../components/VisaNotesSection';
import { formatAirlineAmount, getCurrencyLabel } from '../utils/currencyUtils';
import CurrencyMinimumBalanceSection from '../components/CurrencyMinimumBalanceSection';
import useCountries from '../hooks/useCountries';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PATH_TO_TAB = {
  '/super-admin/dashboard': 'dashboard',
  '/super-admin/assign-me': 'assign_me',
  '/super-admin/visa-applications': 'visa_applications',
  '/super-admin/error-pool': 'error_pool',
  '/super-admin/finalize-pool': 'finalize_pool',
  '/super-admin/reassign': 'reassign',
  '/super-admin/airlines': 'airlines',
  '/super-admin/managers': 'managers',
  '/super-admin/currency-minimum-balances': 'currency_minimum_balances',
  '/super-admin/reports': 'reports',
};

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const getTabFromPath = () => PATH_TO_TAB[location.pathname] || 'dashboard';
  const [activeTab, setActiveTab] = useState(getTabFromPath());
  const [stats, setStats] = useState(null);
  const [airlines, setAirlines] = useState([]);
  const [discountDrafts, setDiscountDrafts] = useState({});
  const [discountSavingId, setDiscountSavingId] = useState(null);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showManagerForm, setShowManagerForm] = useState(false);
  const [newManager, setNewManager] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    from_country: [],
    to_country: [],
    airlines: [],
  });
  const { countries, loading: fetchingCountries } = useCountries();
  const [editManager, setEditManager] = useState(null);
  const [visaApplications, setVisaApplications] = useState([]);
  const [visaFilters, setVisaFilters] = useState(DEFAULT_VISA_FILTERS);
  const [errorPoolFilters, setErrorPoolFilters] = useState(ERROR_POOL_FILTERS);
  const [finalizePoolFilters, setFinalizePoolFilters] = useState(FINALIZE_POOL_FILTERS);
  const [availableApplications, setAvailableApplications] = useState([]);
  const [reassignApplications, setReassignApplications] = useState([]);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [eligibleManagers, setEligibleManagers] = useState([]);
  const [selectedNewManagerId, setSelectedNewManagerId] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);
  const [eligibleManagersLoading, setEligibleManagersLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [historyApplication, setHistoryApplication] = useState(null);
  const [selectedCreditAirlineId, setSelectedCreditAirlineId] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [airlineActionMenu, setAirlineActionMenu] = useState(null);
  const [managerActionMenu, setManagerActionMenu] = useState(null);

  const filteredVisaApplications = useMemo(
    () => filterVisaApplications(visaApplications, visaFilters),
    [visaApplications, visaFilters]
  );

  useEffect(() => {
    const init = async () => {
      const tab = getTabFromPath();
      setActiveTab(tab);
      await fetchDashboardData(false);
      if (tab === 'airlines') await fetchAirlines();
      if (tab === 'managers') {
        await fetchManagers();
        await fetchAirlines();
      }
      if (tab === 'assign_me') await fetchAvailableApplications(false);
      if (tab === 'visa_applications') await fetchVisaApplications(false);
      if (tab === 'error_pool' || tab === 'finalize_pool') await fetchVisaApplications(false);
      if (tab === 'reassign') await fetchReassignApplications(false);
      if (tab === 'reports') await fetchReports();
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    const tab = getTabFromPath();
    if (tab !== activeTab) {
      setActiveTab(tab);
      handleTabData(tab);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!airlineActionMenu) return;

    const closeMenu = () => setAirlineActionMenu(null);
    document.addEventListener('click', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('click', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [airlineActionMenu]);

  useEffect(() => {
    if (!managerActionMenu) return;

    const closeMenu = () => setManagerActionMenu(null);
    document.addEventListener('click', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('click', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [managerActionMenu]);

  const handleTabData = (tabId) => {
    if (tabId === 'airlines') fetchAirlines();
    if (tabId === 'managers') {
      fetchManagers();
      fetchAirlines();
    }
    if (tabId === 'assign_me') fetchAvailableApplications();
    if (tabId === 'visa_applications') fetchVisaApplications();
    if (tabId === 'error_pool' || tabId === 'finalize_pool') fetchVisaApplications();
    if (tabId === 'reassign') fetchReassignApplications();
    if (tabId === 'reports') fetchReports();
    if (tabId === 'dashboard') fetchDashboardData();
  };

  const fetchReports = async () => {
    setReportLoading(true);
    try {
      const res = await axios.get(`${API_URL}/super-admin/reports`);
      setReport(res.data);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  };

  const fetchDashboardData = async (manageLoading = true) => {
    try {
      const res = await axios.get(`${API_URL}/super-admin/dashboard`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const fetchAirlines = async () => {
    try {
      const res = await axios.get(`${API_URL}/super-admin/airlines`);
      setAirlines(res.data);
      const drafts = {};
      (res.data || []).forEach((airline) => {
        drafts[airline.id] = String(
          airline.discount_percentage === undefined || airline.discount_percentage === null
            ? 0
            : airline.discount_percentage
        );
      });
      setDiscountDrafts(drafts);
    } catch (err) {
      console.error('Failed to fetch airlines:', err);
      setError('Failed to load airlines');
    }
  };

  const fetchManagers = async () => {
    try {
      const res = await axios.get(`${API_URL}/super-admin/managers`);
      setManagers(res.data);
    } catch (err) {
      console.error('Failed to fetch managers:', err);
      setError('Failed to load managers');
    }
  };

  const fetchAvailableApplications = async (manageLoading = true) => {
    try {
      const res = await axios.get(`${API_URL}/super-admin/visa-applications/available`);
      setAvailableApplications(res.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch available applications:', err);
      setError(err.response?.data?.error || 'Failed to load available visa applications');
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const fetchVisaApplications = async (manageLoading = true) => {
    try {
      const res = await axios.get(`${API_URL}/super-admin/visa-applications`);
      setVisaApplications(res.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch visa applications:', err);
      setError(err.response?.data?.error || 'Failed to load visa applications');
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const fetchReassignApplications = async (manageLoading = true) => {
    try {
      const res = await axios.get(`${API_URL}/super-admin/reassign/applications`);
      setReassignApplications(res.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch reassign applications:', err);
      setError(err.response?.data?.error || 'Failed to load assigned applications');
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const openReassignModal = async (application) => {
    setReassignTarget(application);
    setSelectedNewManagerId('');
    setEligibleManagers([]);
    setEligibleManagersLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${API_URL}/super-admin/reassign/applications/${application.id}/eligible-managers`
      );
      setEligibleManagers(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load eligible managers');
      setReassignTarget(null);
    } finally {
      setEligibleManagersLoading(false);
    }
  };

  const closeReassignModal = () => {
    if (reassignLoading) return;
    setReassignTarget(null);
    setSelectedNewManagerId('');
    setEligibleManagers([]);
  };

  const confirmReassign = async () => {
    if (!reassignTarget || !selectedNewManagerId) {
      setError('Select a manager to reassign this application to');
      return;
    }

    setReassignLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${API_URL}/super-admin/reassign`, {
        visa_application_id: reassignTarget.id,
        new_manager_id: Number(selectedNewManagerId),
      });
      setSuccess(res.data.message);
      closeReassignModal();
      await fetchReassignApplications(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reassign application');
    } finally {
      setReassignLoading(false);
    }
  };

  const toggleAirlineStatus = async (id) => {
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/super-admin/airlines/${id}/toggle-status`);
      setSuccess('Airline status updated');
      fetchAirlines();
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update airline status');
    }
  };

  const handleDiscountDraftChange = (airlineId, value) => {
    setDiscountDrafts((prev) => ({ ...prev, [airlineId]: value }));
  };

  const saveAirlineDiscount = async (airline) => {
    const raw = discountDrafts[airline.id];
    const discount = Number(raw);

    if (!Number.isFinite(discount) || discount < 0 || discount > 100) {
      setError('Discount percentage must be between 0 and 100');
      return;
    }

    setError('');
    setSuccess('');
    setDiscountSavingId(airline.id);
    try {
      const res = await axios.put(`${API_URL}/super-admin/airlines/${airline.id}/discount`, {
        discount_percentage: discount,
      });
      setSuccess(res.data.message);
      setAirlines((prev) =>
        prev.map((item) =>
          item.id === airline.id
            ? { ...item, discount_percentage: res.data.discount_percentage }
            : item
        )
      );
      setDiscountDrafts((prev) => ({
        ...prev,
        [airline.id]: String(res.data.discount_percentage),
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update discount');
    } finally {
      setDiscountSavingId(null);
    }
  };

  const deleteAirline = async (airline) => {
    const confirmed = window.confirm(
      `Delete ${airline.name}? This will permanently remove the airline and all related data.`
    );
    if (!confirmed) return;

    setError('');
    setSuccess('');
    try {
      const res = await axios.delete(`${API_URL}/super-admin/airlines/${airline.id}`);
      setSuccess(res.data.message);
      if (String(selectedCreditAirlineId) === String(airline.id)) {
        setSelectedCreditAirlineId('');
      }
      fetchAirlines();
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete airline');
    }
  };

  const handleAddManager = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!newManager.name || !newManager.email || !newManager.password) {
      setError('Name, email and password are required');
      return;
    }
    try {
      await axios.post(`${API_URL}/super-admin/managers`, {
        ...newManager,
        from_country: serializeCountryIds(newManager.from_country),
        to_country: serializeCountryIds(newManager.to_country),
        airlines: serializeCountryIds(newManager.airlines),
      });
      setSuccess('Manager added successfully');
      setNewManager({
        name: '',
        email: '',
        password: '',
        phone: '',
        from_country: [],
        to_country: [],
        airlines: [],
      });
      setShowManagerForm(false);
      fetchManagers();
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add manager');
    }
  };

  const toggleManagerStatus = async (id) => {
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/super-admin/managers/${id}/toggle-status`);
      setSuccess('Manager status updated');
      fetchManagers();
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update manager status');
    }
  };

  const handleEditManager = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!editManager?.name || !editManager?.email) {
      setError('Name and email are required');
      return;
    }
    try {
      await axios.put(`${API_URL}/super-admin/managers/${editManager.id}`, {
        name: editManager.name,
        email: editManager.email,
        phone: editManager.phone,
        password: editManager.password || undefined,
        from_country: serializeCountryIds(editManager.from_country),
        to_country: serializeCountryIds(editManager.to_country),
        airlines: serializeCountryIds(editManager.airlines),
      });
      setSuccess('Manager updated successfully');
      setEditManager(null);
      fetchManagers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update manager');
    }
  };

  const deleteManager = async (manager) => {
    const confirmed = window.confirm(
      `Delete ${manager.name}? This will permanently remove this manager account.`
    );
    if (!confirmed) return;

    setError('');
    setSuccess('');
    try {
      const res = await axios.delete(`${API_URL}/super-admin/managers/${manager.id}`);
      setSuccess(res.data.message);
      fetchManagers();
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete manager');
    }
  };

  const handleGrantCredits = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!selectedCreditAirlineId || !creditAmount || parseFloat(creditAmount) <= 0) {
      setError('Select an airline and enter a valid credit amount');
      return;
    }
    try {
      const res = await axios.post(
        `${API_URL}/super-admin/airlines/${selectedCreditAirlineId}/credits`,
        { amount: parseFloat(creditAmount) }
      );
      setSuccess(res.data.message);
      setCreditAmount('');
      fetchAirlines();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to grant credits');
    }
  };

  const selectedCreditAirline = airlines.find(
    (airline) => String(airline.id) === String(selectedCreditAirlineId)
  );

  const renderMessages = () => (
    <>
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
          {success}
        </div>
      )}
    </>
  );

  const renderDashboard = () => (
    <>
      <div className="mb-8">
        <h1 className="primary-title">Super Admin Dashboard</h1>
        <p className="text-gray-500 mt-1">Manage all airlines and system users</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="stats-card">
          <p className="text-gray-500 text-sm font-medium">Total Airlines</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalAirlines || 0}</p>
          <div className="mt-3 text-xs text-gray-400">Registered airlines</div>
        </div>

        <div className="stats-card">
          <p className="text-gray-500 text-sm font-medium">Active Airlines</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.activeAirlines || 0}</p>
          <div className="mt-3 text-xs text-gray-400">Currently active</div>
        </div>

        <div className="stats-card">
          <p className="text-gray-500 text-sm font-medium">Managers</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalManagers || 0}</p>
          <div className="mt-3 text-xs text-gray-400">Internal Vughy managers</div>
        </div>

        <div className="stats-card">
          <p className="text-gray-500 text-sm font-medium">Visa Applications</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats?.totalVisaApplications || 0}</p>
          <div className="mt-3 text-xs text-gray-400">Across all airlines</div>
        </div>
      </div>
    </>
  );

  const toggleAirlineActionMenu = (event, airline) => {
    event.stopPropagation();

    if (airlineActionMenu?.airline.id === airline.id) {
      setAirlineActionMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setAirlineActionMenu({
      airline,
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - 176),
    });
  };

  const renderAirlineActionMenu = () => {
    if (!airlineActionMenu) return null;

    const { airline, top, left } = airlineActionMenu;

    return createPortal(
      <div
        className="fixed z-50 w-44 bg-white border border-gray-200 rounded-xl shadow-lg py-1"
        style={{ top, left }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            toggleAirlineStatus(airline.id);
            setAirlineActionMenu(null);
          }}
          className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
            airline.status === 'active'
              ? 'text-red-600 hover:bg-red-50'
              : 'text-green-600 hover:bg-green-50'
          }`}
        >
          {airline.status === 'active' ? 'Deactivate' : 'Activate'}
        </button>
        <button
          type="button"
          onClick={() => {
            setAirlineActionMenu(null);
            deleteAirline(airline);
          }}
          className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Delete
        </button>
      </div>,
      document.body
    );
  };

  const renderAirlines = () => (
    <>
      <div className="mb-8">
        <h1 className="primary-title">Airlines</h1>
        <p className="text-gray-500 mt-1">View and manage all registered airlines</p>
      </div>

      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Free Credits</h2>
        <p className="text-sm text-gray-500 mb-4">Select an airline and grant free credits to their account</p>
        <form onSubmit={handleGrantCredits} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Airline</label>
            <select
              value={selectedCreditAirlineId}
              onChange={(e) => setSelectedCreditAirlineId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
              required
            >
              <option value="">Select airline</option>
              {airlines.map((airline) => (
                <option key={airline.id} value={airline.id}>
                  {airline.name} ({airline.iata_number})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Credit Amount
              {selectedCreditAirline?.currency
                ? ` (${getCurrencyLabel(selectedCreditAirline.currency)})`
                : ''}
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
              placeholder="0.00"
              required
            />
          </div>
          <div>
            <button
              type="submit"
              disabled={!selectedCreditAirlineId || airlines.length === 0}
              className="w-full px-4 py-2 text-sm font-medium bg-primary hover:bg-primary-hover disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
            >
              Grant Free Credits
            </button>
          </div>
        </form>
        {selectedCreditAirline && (
          <p className="mt-3 text-sm text-gray-500">
            Current balance for <span className="font-medium text-gray-700">{selectedCreditAirline.name}</span>:{' '}
            <span className="font-medium text-gray-900">
              {formatAirlineAmount(selectedCreditAirline.balance, selectedCreditAirline.currency)}
            </span>
          </p>
        )}
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Name</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Email</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">IATA</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Country</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Balance</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Visa Apps</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">Discount %</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {airlines.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-gray-400">No airlines registered yet</td>
                </tr>
              ) : (
                airlines.map((airline) => (
                  <tr key={airline.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{airline.name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{airline.email}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{airline.iata_number}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{airline.country_name || airline.country}</td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">
                      {formatAirlineAmount(airline.balance, airline.currency)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{airline.visa_application_count || 0}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 min-w-[140px]">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={discountDrafts[airline.id] ?? String(airline.discount_percentage || 0)}
                          onChange={(e) => handleDiscountDraftChange(airline.id, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveAirlineDiscount(airline);
                            }
                          }}
                          className="w-20 px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                          aria-label={`Discount percentage for ${airline.name}`}
                        />
                        <button
                          type="button"
                          onClick={() => saveAirlineDiscount(airline)}
                          disabled={discountSavingId === airline.id}
                          className="px-2.5 py-1.5 text-xs font-medium bg-primary hover:bg-primary-hover disabled:bg-gray-300 text-white rounded-lg transition-colors"
                        >
                          {discountSavingId === airline.id ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        airline.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {airline.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <ActionsMenuButton
                        label={`Actions for ${airline.name}`}
                        expanded={airlineActionMenu?.airline.id === airline.id}
                        onClick={(e) => toggleAirlineActionMenu(e, airline)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {renderAirlineActionMenu()}
    </>
  );

  const renderApplicationDetailModal = () => {
    if (!selectedApplication) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedApplication(null)}>
        <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Application Details</h3>
            <button type="button" onClick={() => setSelectedApplication(null)} className="text-gray-400 hover:text-gray-600">×</button>
          </div>
          <div className="space-y-3 text-sm">
            <div><span className="font-medium text-gray-700">Airline:</span> {selectedApplication.airline_name}</div>
            <div><span className="font-medium text-gray-700">Passenger ID:</span> {selectedApplication.passenger_id}</div>
            <div><span className="font-medium text-gray-700">From:</span> {selectedApplication.from_country_name || selectedApplication.from_country}</div>
            <div><span className="font-medium text-gray-700">To:</span> {selectedApplication.to_country_name || selectedApplication.to_country}</div>
            <div><span className="font-medium text-gray-700">Visa Type:</span> {selectedApplication.visa_type}</div>
            <div><span className="font-medium text-gray-700">Application Status:</span> {formatStatusLabel(selectedApplication.application_status)}</div>
            {selectedApplication.status_description && (
              <div><span className="font-medium text-gray-700">Status Description:</span> {selectedApplication.status_description}</div>
            )}
            <VisaNotesSection application={selectedApplication} role={user?.role} />
            <div>
              <p className="font-medium text-gray-700 mb-2">Application Data</p>
              <pre className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(selectedApplication.customer_visa_application_data, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAssignMe = () => (
    <>
      <div className="mb-8">
        <h1 className="primary-title">Assign Me</h1>
        <p className="text-gray-500 mt-1">Unassigned visa applications across the project (view only)</p>
      </div>

      {renderApplicationDetailModal()}

      <div className="card">
        <p className="text-sm text-gray-500 mb-6">
          {availableApplications.length} unassigned application{availableApplications.length === 1 ? '' : 's'}
        </p>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Airline</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Passenger ID</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Applicant</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">From</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">To</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Visa Type</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Submitted</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {availableApplications.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-gray-400">
                    No unassigned visa applications.
                  </td>
                </tr>
              ) : (
                availableApplications.map((app) => (
                  <tr key={app.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{app.airline_name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.passenger_id}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{getApplicantName(app.customer_visa_application_data)}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.from_country_name || app.from_country}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.to_country_name || app.to_country}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{app.visa_type}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(app.application_status)}`}>
                        {formatStatusLabel(app.application_status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">{new Date(app.created_at).toLocaleDateString()}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedApplication(app)}
                          className="text-sm px-3 py-1.5 rounded-lg font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => setHistoryApplication(app)}
                          className="text-sm px-3 py-1.5 rounded-lg font-medium bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors"
                        >
                          History
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const renderVisaApplications = () => (
      <>
        <div className="mb-8">
          <h1 className="primary-title">Visa Applications</h1>
          <p className="text-gray-500 mt-1">All visa applications across the project (view only)</p>
        </div>

        {renderApplicationDetailModal()}

        <VisaApplicationFilters
          applications={visaApplications}
          filters={visaFilters}
          onChange={setVisaFilters}
          onClear={() => setVisaFilters(DEFAULT_VISA_FILTERS)}
          showAirlineFilter
          totalCount={visaApplications.length}
          filteredCount={filteredVisaApplications.length}
        />

        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Airline</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Passenger ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Applicant</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">From</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">To</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Visa Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Submitted</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visaApplications.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-8 text-gray-400">
                      No visa applications yet.
                    </td>
                  </tr>
                ) : filteredVisaApplications.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-8 text-gray-400">No applications match your filters.</td>
                  </tr>
                ) : (
                  filteredVisaApplications.map((app) => (
                    <tr key={app.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{app.airline_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.passenger_id}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{getApplicantName(app.customer_visa_application_data)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.from_country_name || app.from_country}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.to_country_name || app.to_country}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.visa_type}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(app.application_status)}`}>
                          {formatStatusLabel(app.application_status)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">{new Date(app.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedApplication(app)}
                            className="text-sm px-3 py-1.5 rounded-lg font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => setHistoryApplication(app)}
                            className="text-sm px-3 py-1.5 rounded-lg font-medium bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors"
                          >
                            History
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
  );

  const toggleManagerActionMenu = (event, manager) => {
    event.stopPropagation();

    if (managerActionMenu?.manager.id === manager.id) {
      setManagerActionMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    setManagerActionMenu({
      manager,
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - 176),
    });
  };

  const renderManagerActionMenu = () => {
    if (!managerActionMenu) return null;

    const { manager, top, left } = managerActionMenu;

    return createPortal(
      <div
        className="fixed z-50 w-44 bg-white border border-gray-200 rounded-xl shadow-lg py-1"
        style={{ top, left }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => {
            setEditManager({
              ...manager,
              password: '',
              from_country: parseCountryIds(manager.from_country),
              to_country: parseCountryIds(manager.to_country),
              airlines: parseCountryIds(manager.airlines),
            });
            setManagerActionMenu(null);
          }}
          className="w-full text-left px-4 py-2.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => {
            toggleManagerStatus(manager.id);
            setManagerActionMenu(null);
          }}
          className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors ${
            manager.status === 'active'
              ? 'text-red-600 hover:bg-red-50'
              : 'text-green-600 hover:bg-green-50'
          }`}
        >
          {manager.status === 'active' ? 'Deactivate' : 'Activate'}
        </button>
        <button
          type="button"
          onClick={() => {
            setManagerActionMenu(null);
            deleteManager(manager);
          }}
          className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Delete
        </button>
      </div>,
      document.body
    );
  };

  const renderManagers = () => (
    <>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="primary-title">Managers</h1>
          <p className="text-gray-500 mt-1">Manage internal Vughy managers</p>
        </div>
        <button
          onClick={() => setShowManagerForm(!showManagerForm)}
          className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary-hover text-white rounded-xl transition-colors"
        >
          {showManagerForm ? 'Cancel' : '+ Add Manager'}
        </button>
      </div>

      {editManager && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditManager(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Manager</h3>
            <form onSubmit={handleEditManager} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editManager.name}
                    onChange={(e) => setEditManager({ ...editManager, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editManager.email}
                    onChange={(e) => setEditManager({ ...editManager, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
                  <input
                    type="text"
                    value={editManager.phone || ''}
                    onChange={(e) => setEditManager({ ...editManager, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password (optional)</label>
                  <input
                    type="password"
                    value={editManager.password || ''}
                    onChange={(e) => setEditManager({ ...editManager, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                    placeholder="Leave blank to keep current"
                  />
                </div>
                <CountryMultiSelect
                  label="From Country"
                  value={parseCountryIds(editManager.from_country)}
                  onChange={(selected) => setEditManager((prev) => ({ ...prev, from_country: selected }))}
                  countries={countries}
                  loading={fetchingCountries}
                  placeholder="Click to select from countries"
                />
                <CountryMultiSelect
                  label="To Country"
                  value={parseCountryIds(editManager.to_country)}
                  onChange={(selected) => setEditManager((prev) => ({ ...prev, to_country: selected }))}
                  countries={countries}
                  loading={fetchingCountries}
                  placeholder="Click to select to countries"
                />
                <div className="md:col-span-2">
                  <AirlineMultiSelect
                    label="Airlines"
                    value={parseCountryIds(editManager.airlines)}
                    onChange={(selected) => setEditManager((prev) => ({ ...prev, airlines: selected }))}
                    airlines={airlines}
                    loading={!airlines.length}
                    placeholder="Click to select airlines"
                  />
                </div>
              </div>
              <div className="flex gap-3 justify-end mt-4">
                <button
                  type="button"
                  onClick={() => setEditManager(null)}
                  className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary-hover text-white rounded-xl transition-colors"
                >
                  Update Manager
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showManagerForm && (
        <form onSubmit={handleAddManager} className="mb-6 p-5 bg-gray-50 rounded-xl border border-gray-200">
          <h3 className="font-medium text-gray-900 mb-4">Add New Manager</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={newManager.name}
                onChange={(e) => setNewManager({ ...newManager, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                placeholder="Manager name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={newManager.email}
                onChange={(e) => setNewManager({ ...newManager, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                placeholder="manager@email.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={newManager.password}
                onChange={(e) => setNewManager({ ...newManager, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                placeholder="Set password"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
              <input
                type="text"
                value={newManager.phone}
                onChange={(e) => setNewManager({ ...newManager, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                placeholder="Phone number"
              />
            </div>
            <CountryMultiSelect
              label="From Country"
              value={newManager.from_country}
              onChange={(selected) => setNewManager((prev) => ({ ...prev, from_country: selected }))}
              countries={countries}
              loading={fetchingCountries}
              placeholder="Click to select from countries"
            />
            <CountryMultiSelect
              label="To Country"
              value={newManager.to_country}
              onChange={(selected) => setNewManager((prev) => ({ ...prev, to_country: selected }))}
              countries={countries}
              loading={fetchingCountries}
              placeholder="Click to select to countries"
            />
            <div className="md:col-span-2">
              <AirlineMultiSelect
                label="Airlines"
                value={newManager.airlines}
                onChange={(selected) => setNewManager((prev) => ({ ...prev, airlines: selected }))}
                airlines={airlines}
                loading={!airlines.length}
                placeholder="Click to select airlines"
              />
            </div>
          </div>
          <button type="submit" className="mt-4 px-6 py-2 text-sm font-medium bg-primary hover:bg-primary-hover text-white rounded-xl transition-colors">
            Add Manager
          </button>
        </form>
      )}

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Name</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Email</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Phone</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">From</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">To</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Airlines</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Joined</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {managers.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-gray-400">No managers yet</td>
                </tr>
              ) : (
                managers.map((manager) => (
                  <tr key={manager.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{manager.name}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{manager.email}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{manager.phone || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{manager.from_country_name || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{manager.to_country_name || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{manager.airline_names || '-'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        manager.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {manager.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {new Date(manager.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <ActionsMenuButton
                        label={`Actions for ${manager.name}`}
                        expanded={managerActionMenu?.manager.id === manager.id}
                        onClick={(e) => toggleManagerActionMenu(e, manager)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {renderManagerActionMenu()}
    </>
  );

  const renderReassign = () => {
    const reassignModal = reassignTarget ? (
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        onClick={closeReassignModal}
      >
        <div
          className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Reassign Application</h3>
          <p className="text-sm text-gray-600 mb-4">
            {reassignTarget.airline_name} — {reassignTarget.passenger_id}
          </p>
          <div className="space-y-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Current manager:</span>{' '}
              {reassignTarget.manager_name}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">New manager</label>
              {eligibleManagersLoading ? (
                <p className="text-gray-400 text-sm">Loading eligible managers...</p>
              ) : eligibleManagers.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  No other active managers match this application&apos;s route and airlines.
                </p>
              ) : (
                <select
                  value={selectedNewManagerId}
                  onChange={(e) => setSelectedNewManagerId(e.target.value)}
                  disabled={reassignLoading}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
                >
                  <option value="">Select a manager</option>
                  {eligibleManagers.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.name} ({manager.email})
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-6">
            <button
              type="button"
              onClick={closeReassignModal}
              disabled={reassignLoading}
              className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmReassign}
              disabled={reassignLoading || !selectedNewManagerId || eligibleManagers.length === 0}
              className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary-hover text-white rounded-xl transition-colors disabled:opacity-50"
            >
              {reassignLoading ? 'Reassigning...' : 'Confirm Reassign'}
            </button>
          </div>
        </div>
      </div>
    ) : null;

    return (
      <>
        {reassignModal}

        <div className="mb-8">
          <h1 className="primary-title">Reassign</h1>
          <p className="text-gray-500 mt-1">
            Move assigned visa applications from one manager to another
          </p>
        </div>

        <div className="card">
          <p className="text-sm text-gray-500 mb-6">
            {reassignApplications.length} assigned application{reassignApplications.length === 1 ? '' : 's'}
          </p>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Airline</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Passenger ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Applicant</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Current Manager</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Assigned</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reassignApplications.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-400">
                      No manager-assigned applications to reassign.
                    </td>
                  </tr>
                ) : (
                  reassignApplications.map((app) => (
                    <tr key={app.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{app.airline_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.passenger_id}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {getApplicantName(app.customer_visa_application_data)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.manager_name}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(app.application_status)}`}>
                          {formatStatusLabel(app.application_status)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {new Date(app.assigned_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          type="button"
                          onClick={() => openReassignModal(app)}
                          className="text-sm px-3 py-1.5 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >
                          Reassign
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderReports = () => (
    <ReportsSection
      title="System Reports"
      subtitle="Complete overview of visa applications, airlines, and system activity"
      report={report}
      loading={reportLoading}
    />
  );

  const renderErrorPool = () => (
    <>
      {renderApplicationDetailModal()}
      <VisaApplicationsPoolSection
        title="Error Pool"
        subtitle="All visa applications with error status across the project"
        applications={visaApplications}
        filters={errorPoolFilters}
        onFiltersChange={setErrorPoolFilters}
        onClearFilters={() => setErrorPoolFilters(ERROR_POOL_FILTERS)}
        showAirlineFilter
        showAirlineColumn
        onView={setSelectedApplication}
        onViewHistory={setHistoryApplication}
        emptyMessage="No error applications in the pool."
      />
    </>
  );

  const renderFinalizePool = () => (
    <>
      {renderApplicationDetailModal()}
      <VisaApplicationsPoolSection
        title="Finalize Pool"
        subtitle="All finalized visa applications across the project"
        applications={visaApplications}
        filters={finalizePoolFilters}
        onFiltersChange={setFinalizePoolFilters}
        onClearFilters={() => setFinalizePoolFilters(FINALIZE_POOL_FILTERS)}
        showAirlineFilter
        showAirlineColumn
        onView={setSelectedApplication}
        onViewHistory={setHistoryApplication}
        emptyMessage="No finalized applications in the pool."
      />
    </>
  );

  if (loading && activeTab !== 'reports' && activeTab !== 'visa_applications' && activeTab !== 'error_pool' && activeTab !== 'finalize_pool' && activeTab !== 'assign_me' && activeTab !== 'reassign' && activeTab !== 'currency_minimum_balances') {
    return (
      <Layout>
        <LoadingSpinner text="Loading dashboard..." />
      </Layout>
    );
  }

  return (
    <Layout>
      {renderMessages()}

      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'assign_me' && renderAssignMe()}
      {activeTab === 'visa_applications' && renderVisaApplications()}
      {activeTab === 'error_pool' && renderErrorPool()}
      {activeTab === 'finalize_pool' && renderFinalizePool()}
      {activeTab === 'reassign' && renderReassign()}
      {activeTab === 'airlines' && renderAirlines()}
      {activeTab === 'managers' && renderManagers()}
      {activeTab === 'currency_minimum_balances' && <CurrencyMinimumBalanceSection />}
      {activeTab === 'reports' && renderReports()}

      {historyApplication && (
        <VisaApplicationHistoryModal
          application={historyApplication}
          historyUrl={getVisaHistoryUrl(historyApplication.id, user?.role)}
          onClose={() => setHistoryApplication(null)}
        />
      )}
    </Layout>
  );
};

export default SuperAdminDashboard;
